import { CalendarService } from '@b2b/common/services/calendar';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { UIBModal } from '@b2b/types/core';
import { TODO } from '@b2b/types/core/utils';
import angular, { ITimeoutService } from 'angular';
import moment from 'moment';
import {
  OrdersModalsPrivateEventServiceModule,
  PrivateEventService,
} from '../../../../orders/modals/privateEvent/controllers/PrivateEventService';
import { PartnerDetail } from '@b2b/common/services/api/partnersApi/types';

interface ResourcesList {
  resourceId: string;
  resourceName: string;
  description: string;
  ownerId: string;
  resourceCategory: string;
  match: boolean;
  available: boolean;
  conflicts: boolean;
  count: number;
  resourceTemplateNames: ['Tablet', 'Camera'];
  /**
   * `resourceTemplateNames` joined by `, `
   * @example ['Tablet', 'Camera'] --> 'Tablet, Camera'
   */
  resourceTemplateNamesParsed: 'Tablet, Camera';
}

export interface CreatePrivateEventModalData {
  startTime: TODO;
  accountId: number | null;
  accountCategoryRelation: PartnerDetail['accountCategoryRelation'] | null;
  preventAddWhenOutOfCapacity?: boolean;
}

export function createPrivateEventModalConfig(resolveModalData: () => CreatePrivateEventModalData) {
  return {
    templateUrl: 'views/application/views/programs/modals/createPrivateEvent/createPrivateEvent.tpl.html',
    size: 'md',
    controller: 'CreatePrivateEventController',
    resolve: {
      MODAL_DATA: resolveModalData(),
    },
  };
}

CreatePrivateEventController.$inject = [
  '$scope',
  '$timeout',
  '$uibModalInstance',
  'PrivateEventService',
  'UserPreferencesService',
  'CalendarService',
  'ValidateService',
  'HelpersService',
  'PRIVATE_EVENT_MODEL',
  'PRIVATE_EVENT_SCHEDULE_TYPE',
  'PRIVATE_EVENT_PAYLOAD',
  'PRIVATE_EVENT_EXPIRE_OPTIONS',
  'MODAL_DATA',
];

function CreatePrivateEventController(
  $scope: TODO & { createPrivateEvent: TODO },
  $timeout: ITimeoutService,
  $uibModalInstance: UIBModal<TODO>,
  PrivateEventService: PrivateEventService,
  UserPreferencesService: UserPreferencesService,
  CalendarService: CalendarService,
  ValidateService: TODO,
  HelpersService: TODO,
  PRIVATE_EVENT_MODEL: TODO,
  PRIVATE_EVENT_SCHEDULE_TYPE: TODO,
  PRIVATE_EVENT_PAYLOAD: TODO,
  PRIVATE_EVENT_EXPIRE_OPTIONS: TODO,
  MODAL_DATA: CreatePrivateEventModalData,
) {
  const ctrl = {
    model: angular.copy(PRIVATE_EVENT_MODEL),
    accountId: MODAL_DATA.accountId,
    accountCategoryRelation: MODAL_DATA.accountCategoryRelation,

    hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
    timesTab: PRIVATE_EVENT_SCHEDULE_TYPE.private.name,
    privateSchedule: null as { id: TODO; resourceRequests: TODO[]; hours?: TODO[]; salesChannels: TODO[] } | null,
    privateScheduleTime: null as { hour: TODO; minutes: TODO } | null,
    privateScheduleTimes: [] as TODO[],
    privateDuration: null as TODO | null,

    isLoading: false,
    isSaving: false,
    isSubmitted: false,

    showExtended: false,
    showTime: false,
    showResources: false,
    isSelected: false,

    errormessage: 'A Private Event Schedule is required.',
    errors: {},

    expireOptions: angular.copy(PRIVATE_EVENT_EXPIRE_OPTIONS),

    eventTemplates: [] as TODO[],
    priceLists: [] as TODO[],
    discounts: [] as TODO[],

    spectrumConfig: {
      titleText: 'Tile color',
      color: '#d1d1d1',
    },

    organization: false,

    startTime: MODAL_DATA.startTime || new Date(),

    eventStartHour: new Date(),
    duration: { hour: '0', minutes: '00' },
    resources: [] as TODO[],
    resourcesCount: 0,
    selectedResourcesList: {} as Record<string, ResourcesList>,
    resourceRequests: [] as TODO[],

    init: function () {
      ctrl.model.date = CalendarService.parseDate(ctrl.startTime);

      PrivateEventService.getTemplateNames(false).then((res) => {
        ctrl.eventTemplates = res.data.payload.list;
      });

      $scope.$watchGroup(
        [
          'createPrivateEvent.model.name',
          'createPrivateEvent.model.date',
          'createPrivateEvent.model.capacity',
          'createPrivateEvent.model.priceList',
          'createPrivateEvent.model.description',
          'createPrivateEvent.model.timeMin',
          'createPrivateEvent.model.durationMin',
        ],
        function () {
          ctrl.tryClearErrors();
        },
      );

      $scope.$watch('createPrivateEvent.model.timeHr', function (newVal, oldVal) {
        if (newVal !== null && ctrl.model.timeMin === null) {
          ctrl.model.timeMin = '00';
        }
        ctrl.tryClearErrors();
      });

      $scope.$watch('createPrivateEvent.model.durationHr', function (newVal, oldVal) {
        if (newVal !== null && ctrl.model.durationMin === null) {
          ctrl.model.durationMin = '00';
        }
        ctrl.tryClearErrors();
      });

      $scope.$watchGroup(
        [
          'createPrivateEvent.timesTab',
          'createPrivateEvent.privateScheduleTime',
          'createPrivateEvent.model.timeHr',
          'createPrivateEvent.model.timeMin',
          'createPrivateEvent.model.timeAm',
          'createPrivateEvent.model.durationHr',
          'createPrivateEvent.model.durationMin',
        ],
        () => {
          ctrl.eventStartHour = new Date(ctrl.getSelectedTime());

          ctrl.duration = {
            hour: ctrl.model.durationHr || '0',
            minutes: ctrl.model.durationMin || '00',
          };
        },
      );
    },

    getSelectedTime: function () {
      const model = ctrl.model;
      const d = model.date ? moment(model.date) : moment();
      const timeAm = model.timeAm ? 'AM' : 'PM';
      const isPrivateSchedule = ctrl.privateSchedule && ctrl.isActiveTab('private') && ctrl.privateScheduleTime;
      const militaryTime = isPrivateSchedule
        ? moment()
            .hour(ctrl.privateScheduleTime?.hour)
            .minute(ctrl.privateScheduleTime?.minutes)
            .second(0)
            .format('HH:mm:ss')
        : moment(model.timeHr + ':' + model.timeMin + ':00 ' + timeAm, 'h:mm:ss A').format('HH:mm:ss');
      const dateTime = moment(d.format('YYYY-MM-DD') + 'T' + militaryTime);

      return dateTime.format('YYYY-MM-DDTHH:mm:ssZZ');
    },

    getValidators: function () {
      const priceListName = ctrl.model.priceList ? ctrl.model.priceList.name : '';
      const validators: TODO[] = [
        {
          field: 'private-event-date',
          dateAfterYesterday: { message: 'Invalid date.', test: { value: ctrl.model.date } },
        },
        {
          field: 'private-event-duration-hr',
          greater: { message: ' ', test: { greater: ctrl.model.durationHr, less: -1 } },
        },
        {
          field: 'private-event-duration-min',
          greater: { message: ' ', test: { greater: ctrl.model.durationMin, less: -1 } },
        },
        {
          field: 'private-event-name',
          len: { message: 'A valid event name required.', test: { value: ctrl.model.name, minLength: 2 } },
        },
        {
          field: 'private-event-max-capacity',
          greater: { message: 'Enter a valid max capacity value', test: { greater: ctrl.model.capacity, less: 0 } },
        },
        {
          field: 'private-event-price-list',
          len: { message: 'Please select a price list', test: { value: priceListName } },
        },
        {
          field: 'private-event-description',
          len: { message: 'A valid event description is required', test: { value: ctrl.model.description } },
        },
        {
          field: 'private-event-expiration-days',
          number: { message: 'A valid ticket expire # days is required', test: ctrl.model.expireDays },
        },
      ];

      if (ctrl.isActiveTab('standard') || !ctrl.hasPrivateSchedulesTimes()) {
        validators.splice(
          1,
          0,
          {
            field: 'private-event-time-hr',
            greater: { message: ' ', test: { greater: ctrl.model.timeHr, less: 0 } },
          },
          {
            field: 'private-event-time-min',
            greater: { message: ' ', test: { greater: ctrl.model.timeMin, less: -1 } },
          },
        );
      }

      return validators;
    },

    isValid: function (field) {
      // Check if submitted or bypass validation on command
      if (ctrl.isSubmitted) {
        const text = ctrl.model[field] ? ctrl.model[field].name : '';
        return ValidateService.validate([{ len: { value: text } }]);
      }

      return true;
    },

    searchTemplates: function (search) {
      PrivateEventService.getTemplateNames(false, search).then((res) => {
        ctrl.eventTemplates = res.data.payload.list;
        $scope.$broadcast('CustomTypeahead::FilterResults', { field: 'combobox-input-' + 'event-templates' });
      });
    },

    setModelFromTemplate: function (template) {
      const schedules = template.schedules || [];
      const schedule = schedules[0] || {};
      const days = schedule.days || [];
      const startTime = template.startTime || new Date().getTime();
      const date = new Date(startTime);
      const duration = schedule.duration || {};
      const hours = schedule.hours || [];
      const hoursMinutes = hours[0] || {};
      const hour = hoursMinutes.hour || 0;
      const ticketDetails = template.ticketDetails || {};
      const priceLists = template.priceLists || [];
      const priceList = priceLists[0] || template.priceList || {};
      const model = ctrl.model;

      ctrl.resetPrivateSchedule();

      model.templateId = template.id;

      model.name = template.name;
      model.description = template.description;
      model.shortDescription = template.shortDescription;
      model.emailConfirmationMessage = template.emailConfirmationMessage;
      model.emailCancellationMessage = template.emailCancellationMessage;
      model.addOns = template.addOns;

      if (template.colorCategory) {
        model.colorCategory = template.colorCategory;
        ctrl.spectrumConfig.color = template.colorCategory.backgroundColor;
      }

      if (template.customFields) {
        model.customFields = template.customFields;
      }

      if (template.GLCode) {
        model.GLCode = template.GLCode;
      }

      if (template.emailNotify) {
        model.emailNotify = template.emailNotify;
      }

      if (template.onlinePaymentsForOrgOrdersEmailNotify) {
        model.onlinePaymentsForOrgOrdersEmailNotify = template.onlinePaymentsForOrgOrdersEmailNotify;
      }

      if (schedules.length) {
        schedules.forEach((item) => {
          if (item.type === PRIVATE_EVENT_SCHEDULE_TYPE.private.name) {
            ctrl.privateSchedule = item;

            ctrl.model.date = CalendarService.parseDate(ctrl.startTime);
            ctrl.model.durationHr =
              item.duration.hour || (!item.duration.hour && item.duration.minutes) ? item.duration.hour : 1;
            ctrl.model.durationMin = item.duration.minutes || 0;
            ctrl.model.capacity = item.capacity;
            ctrl.model.scheduleName = item.name;

            ctrl.privateDuration = angular.copy(item.duration);

            ctrl.showTime = true;

            ctrl.setPrivateScheduleTimes();

            if (ctrl.privateSchedule?.resourceRequests?.length) {
              ctrl.resourceRequests = ctrl.privateSchedule.resourceRequests.slice();

              $timeout(() => {
                ctrl.showResources = true;
              });
            }
          }
        });
      }

      model.priceList = priceList;
      $scope.createPrivateEvent.priceLists.forEach((item) => {
        if (item.id === priceList.id || item.id === priceList.priceListId) {
          model.priceList = item;
        }
      });

      ctrl.isSelected = true;
    },

    resetPrivateSchedule: function () {
      ctrl.privateSchedule = null;
      ctrl.privateScheduleTimes = [];
      ctrl.privateScheduleTime = null;
      ctrl.privateDuration = null;
    },

    setPrivateScheduleTimes: function () {
      if (ctrl.hasPrivateSchedulesTimes()) {
        ctrl.privateScheduleTimes = ensureArray(ctrl.privateSchedule?.hours).map((opening) => {
          const date = new Date();

          date.setHours(opening.hour);
          date.setMinutes(opening.minutes);
          date.setSeconds(0);
          date.setMilliseconds(0);

          opening.id = date;
          opening.name = CalendarService.toFormat(CalendarService.parseDate(date), ctrl.hourFormat);
          opening.date = date;

          return opening;
        });

        if (ctrl.privateScheduleTimes.length) {
          ctrl.privateScheduleTime = ctrl.privateScheduleTimes[0];
        }

        ctrl.showExtended = true;
      }
    },

    hasPrivateSchedulesTimes: function () {
      return ctrl.privateSchedule?.hours?.length;
    },

    validTest: function () {
      if (ctrl.isSelected && !ctrl.hasPrivateSchedulesTimes()) {
        ctrl.errormessage = 'A Private Event Schedule is required.';
        return false;
      }

      if ( ctrl.isSelected && ctrl.preventAddWhenOutOfCapacity()) {
        ctrl.errormessage = 'This Event can’t be added because its private event schedule capacity is 0. Please increase the capacity to continue.';
        return false;
      }

      return true;
    },

    cancel: function () {
      $uibModalInstance.dismiss();
    },

    isActiveTab: function (type) {
      return type === ctrl.timesTab;
    },

    preventAddWhenOutOfCapacity: function () {
      return MODAL_DATA.preventAddWhenOutOfCapacity && ctrl.model.capacity === 0;
    },

    isSaveDisabled: function () {
      return ctrl.isSaving || !ctrl.showExtended || !ctrl.hasPrivateSchedulesTimes() || ctrl.preventAddWhenOutOfCapacity();
    },

    generatePayload: function () {
      const model = ctrl.model;
      const d = moment(model.date);
      const timeAm = model.timeAm ? 'AM' : 'PM';
      const isPrivateSchedule = ctrl.privateSchedule && ctrl.isActiveTab('private') && ctrl.privateScheduleTime;
      const militaryTime = isPrivateSchedule
        ? moment()
            .hour(ctrl.privateScheduleTime?.hour)
            .minute(ctrl.privateScheduleTime?.minutes)
            .second(0)
            .format('HH:mm:ss')
        : moment(model.timeHr + ':' + model.timeMin + ':00 ' + timeAm, 'h:mm:ss A').format('HH:mm:ss');
      const dateTime = moment(d.format('YYYY-MM-DD') + 'T' + militaryTime);
      const payload = angular.copy(PRIVATE_EVENT_PAYLOAD);
      const schedule = payload.schedules[0];

      schedule.days = [dateTime.format('YYYY-MM-DD')];

      schedule.effectiveStartTime = dateTime.format('YYYY-MM-DDTHH:mm:ssZZ');

      if (isPrivateSchedule) {
        schedule.hours = [ctrl.privateScheduleTime];
      } else {
        schedule.hours = [
          {
            hour: parseInt(dateTime.format('HH'), 10),
            minutes: parseInt(dateTime.format('mm'), 10),
            quantity: 1,
          },
        ];
      }

      const resourceRequests = ensureArray(ctrl.privateSchedule?.resourceRequests);

      for (const resourceRequest of resourceRequests) {
        const selectedItem = ctrl.selectedResourcesList[resourceRequest.id];

        if (selectedItem == null) {
          continue;
        }

        resourceRequest.eventResource.resourceId = selectedItem.resourceId;
        resourceRequest.eventResource.resourceName = selectedItem.resourceName;
      }

      schedule.resourceRequest = resourceRequests;

      schedule.capacity = model.capacity;
      schedule.duration = {
        hour: model.durationHr,
        minutes: model.durationMin,
      };

      schedule.name = model.scheduleName;

      if (model.addOns) {
        payload.addOns = model.addOns;
      }

      payload.id = model.eventTemplate && model.eventTemplate.id ? model.eventTemplate.id : false;

      payload.scheduleId = ctrl.privateSchedule?.id;

      payload.salesChannels = ensureArray(ctrl.privateSchedule?.salesChannels).map((item) => {
        if (item.name.toLowerCase() === 'web') {
          item.enabled = false;
        }
        return item;
      });

      payload.capacity = model.capacity;

      payload.priceLists = [model.priceList];

      payload.startTime = schedule.effectiveStartTime;

      payload.endTime = moment(payload.startTime)
        .add(model.durationHr, 'hours')
        .add(model.durationMin, 'minutes')
        .format();

      payload.discountId = model.discount && model.discount.id ? model.discount.id : '';

      payload.name = model.name;

      payload.description = HelpersService.strip(model.description, 300) || model.shortDescription;
      payload.shortDescription = model.shortDescription;

      payload.ticketDetails.expireFromEventDate = model.expireOption === ctrl.expireOptions[0];
      payload.ticketDetails.ticketDurationDays = model.expireDays;

      payload.emailConfirmationMessage = model.emailConfirmationMessage;
      payload.emailCancellationMessage = model.emailCancellationMessage;

      payload.colorCategory = model.colorCategory;

      payload.GLCode = model.GLCode;
      payload.emailNotify = model.emailNotify;
      payload.onlinePaymentsForOrgOrdersEmailNotify = model.onlinePaymentsForOrgOrdersEmailNotify;

      if (model.customFields) {
        payload.customFields = model.customFields;
      }

      return payload;
    },

    save: function () {
      ctrl.isSubmitted = true;

      if (!ValidateService.validate(ctrl.getValidators())) {
        ctrl.errors = ValidateService.getErrorHash();
        return;
      }

      ctrl.isSaving = true;
      ctrl.isSubmitted = false;

      ctrl.saveForOrderCheckout();
    },

    saveForOrderCheckout: function () {
      const accountId = ctrl.accountId || null;
      const accountCategoryRelation = ctrl.accountCategoryRelation || null;

      PrivateEventService.addToOrderCheckout(ctrl.generatePayload(), accountId, accountCategoryRelation)
        .then((res) => {
          $uibModalInstance.close(res);
        })
        .finally(() => {
          ctrl.isSaving = false;
        });
    },

    tryClearErrors: function () {
      if (Object.keys(ctrl.errors).length === 0) {
        return;
      }
      $timeout.cancel($scope.validateTimer);
      $scope.validateTimer = $timeout(() => {
        ValidateService.validate(ctrl.getValidators());
        ctrl.errors = ValidateService.getErrorHash();
      }, 2000);
    },

    onTemplateChange: function () {
      const template = ctrl.model.eventTemplate;

      ctrl.resetPrivateSchedule();

      if (template) {
        PrivateEventService.getTemplate(template.id).then((res) => {
          ctrl.setModelFromTemplate(res.data.payload);
        });
        PrivateEventService.getEventTemplateDetail(template.id).then((res) => {
          const payload = res.data.payload || [];
          const templateDetail = payload[0] || {};
          const discountId = templateDetail.discountId;
          const discounts = ctrl.discounts || [];
          discounts.forEach((discount) => {
            if (discount.id === discountId) {
              ctrl.model.discount = discount;
            }
          });

          if (ctrl.model.discount && ctrl.model.discount.id && ctrl.model.priceList && ctrl.model.priceList.id) {
            PrivateEventService.getEventTemplateDiscountedPriceList(
              ctrl.model.discount.id,
              ctrl.model.priceList.id,
            ).then((res) => {
              const discountedPriceList = res.data.payload.prices || [];
              if (discountedPriceList.length) {
                ctrl.model.priceList.prices = discountedPriceList;
              }
            });
          }
        });
      } else {
        // Clear all the fields
        ctrl.showExtended = false;
        ctrl.showTime = false;
        ctrl.showResources = false;
        ctrl.isSelected = false;

        ctrl.model = angular.copy(PRIVATE_EVENT_MODEL);
        ctrl.model.date = CalendarService.parseDate(ctrl.startTime);
        ctrl.spectrumConfig.color = ctrl.model.colorCategory.backgroundColor;
      }
    },
  };

  $scope.createPrivateEvent = ctrl;
  ctrl.init();
}

export const CreatePrivateEventModalControllerModule = angular
  .module('acme.view.application.programs.modals.createPrivateEvent.controllers', [
    'acme.common.service.api',
    'acme.view.application.orders.modals.privateEvent.constants',
    OrdersModalsPrivateEventServiceModule,
    'acme.view.application.orders.modals.privateEvent.directive.eventDate',
    'acme.common.directive.dateFieldWithLabel',
    'acme.common.directive.textareaFieldWithLabel',
    'acme.common.directive.resourceAvailability',
    'acme.common.service.calendar',
    'acme.common.service.userPreferences',
    'acme.common.service.helpers',
    'acme.common.service.validate',
  ])
  .controller('CreatePrivateEventController', CreatePrivateEventController).name;
